import React, { createContext, useContext, useReducer } from 'react'
import PropTypes from 'prop-types'

export const SimulationContext = createContext()

export const SimulationProvider = ({ reducer, initialState, children }) => (
  <SimulationContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </SimulationContext.Provider>
)

SimulationProvider.propTypes = {
  reducer: PropTypes.func.isRequired,
  initialState: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
}

export const useSimulation = () => useContext(SimulationContext)

export function buildInitialState(config) {
  const tasks = (config.tasks || []).map((task, index) => ({
    ...task,
    status: index === 0 ? 'active' : 'pending',
  }))

  return {
    guid: config.guid || '',
    title: config.title || '',
    description: config.description || '',
    tasks: tasks,
    pluginName: config.plugin || null,
    pluginConfig: { ...(config.pluginConfig || {}), coursenum: config.coursenum || '' },
    allComplete: false,
    completionUrl: config.completionUrl || '',
    completionButtonText: config.completionButtonText || '',
  }
}

export function simulationReducer(state, action) {
  switch (action.type) {
    case 'COMPLETE_TASK': {
      const taskId = action.payload
      let foundCompleted = false
      const tasks = state.tasks.map(task => {
        if (task.id === taskId && task.status !== 'completed') {
          foundCompleted = true
          return { ...task, status: 'completed' }
        }
        return task
      })

      // Activate the next pending task
      if (foundCompleted) {
        const nextPending = tasks.find(t => t.status === 'pending')
        if (nextPending) {
          const idx = tasks.indexOf(nextPending)
          tasks[idx] = { ...nextPending, status: 'active' }
        }
      }

      const allComplete = tasks.every(t => t.status === 'completed')

      return { ...state, tasks, allComplete }
    }

    case 'ACTIVATE_TASK': {
      const taskId = action.payload
      const tasks = state.tasks.map(task => {
        if (task.id === taskId && task.status === 'pending') {
          return { ...task, status: 'active' }
        }
        return task
      })
      return { ...state, tasks }
    }

    case 'REPORT_PROGRESS': {
      const { taskId, data } = action.payload
      const tasks = state.tasks.map(task => {
        if (task.id === taskId) {
          return { ...task, progress: data }
        }
        return task
      })
      return { ...state, tasks }
    }

    case 'SET_PLUGIN_OUTPUT': {
      return { ...state, _pluginOutput: action.payload }
    }

    case 'RESTORE_STATE': {
      // Rebuild state from a saved snapshot
      return { ...state, ...action.payload }
    }

    case 'RESET': {
      const tasks = state.tasks.map((task, index) => ({
        ...task,
        status: index === 0 ? 'active' : 'pending',
        progress: undefined,
      }))
      return { ...state, tasks, allComplete: false }
    }

    default:
      return state
  }
}
